import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { API, IS_STATIC, inr, slug, waLink } from "../constants/site";
import { STATIC_VISAS } from "../constants/staticData";
import { ChapterHeader } from "./ChapterHeader";

const PASSPORT_IMG =
  "https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxwYXNzcG9ydCUyMHZpc2ElMjBzdGFtcHMlMjB0cmF2ZWx8ZW58MHx8fDE3ODgxNjc2ODd8MA&ixlib=rb-4.1.0&q=85";

const CATS = [
  { id: "all", label: "All Visas", testid: "visa-filter-all" },
  { id: "tourist", label: "Tourist", testid: "visa-filter-tourist" },
  { id: "work", label: "Work & Business", testid: "visa-filter-work" },
];

const VisaRow = ({ v, i }) => (
  <motion.a
    href={waLink(`Hi Gautam Travel! I need help with a ${v.country} ${v.category === "work" ? "work/business" : "tourist"} visa.`)}
    target="_blank"
    rel="noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.5, delay: i * 0.05 }}
    data-testid={`visa-apply-btn-${slug(`${v.country}-${v.category}`)}`}
    className="group -mx-3 flex items-center justify-between gap-6 rounded-xl px-3 py-6 transition-colors duration-300 hover:bg-white/[0.04]"
  >
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display text-2xl">{v.country}</h3>
        <span
          className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] ${
            v.category === "work" ? "bg-teal/15 text-teal" : "bg-sunset/15 text-coral"
          }`}
        >
          {v.category === "work" ? "Work / Business" : "Tourist"}
        </span>
      </div>
      <p className="mt-1.5 text-sm font-light text-white/50">
        {v.processing}
        {v.note ? ` · ${v.note}` : ""}
      </p>
    </div>
    <div className="flex shrink-0 items-center gap-6">
      <p className="hidden font-display text-xl text-white/80 sm:block">{inr(v.price)}</p>
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors duration-300 group-hover:border-teal group-hover:bg-teal group-hover:text-ink">
        <ArrowUpRight className="h-4 w-4" />
      </span>
    </div>
  </motion.a>
);

export const VisaServices = () => {
  const [services, setServices] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    if (IS_STATIC) {
      setServices(STATIC_VISAS);
      return;
    }
    axios
      .get(`${API}/visa-services`)
      .then((r) => setServices(r.data))
      .catch(() => setServices([]));
  }, []);

  const visible = services.filter((v) => cat === "all" || v.category === cat);

  return (
    <section id="visas" data-testid="visa-section" className="relative bg-obsidian px-6 py-28 md:px-10 md:py-36">
      <div className="mx-auto max-w-7xl">
        <ChapterHeader
          index="02"
          title="Visa Services"
          testid="visa-header"
          heading={
            <>
              1000+ approvals. <span className="italic text-teal">Zero guesswork.</span>
            </>
          }
          blurb="Tourist, work and business visas with documentation done right the first time. Honest timelines, rejection-proof checklists, no false promises."
        />

        <div className="grid gap-12 lg:grid-cols-5">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-2"
          >
            <div className="relative overflow-hidden rounded-3xl border border-white/10 lg:sticky lg:top-28">
              <img src={PASSPORT_IMG} alt="Passport pages covered in visa stamps" className="h-[460px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <p className="font-display text-6xl text-white" data-testid="visa-stat-approvals">
                  1000<span className="text-teal">+</span>
                </p>
                <p className="mt-2 text-xs uppercase tracking-[0.3em] text-white/60">Visas approved since 2014</p>
                <p className="mt-4 max-w-xs text-sm font-light text-white/70">
                  Documentation, appointments, biometrics and follow-ups — handled by humans, not bots.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="lg:col-span-3">
            <div className="mb-10 flex flex-wrap gap-3" data-testid="visa-filters">
              {CATS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  data-testid={c.testid}
                  className={`rounded-full px-6 py-2.5 text-xs font-medium uppercase tracking-[0.2em] transition-colors duration-300 ${
                    cat === c.id
                      ? "bg-teal text-ink"
                      : "border border-white/15 text-white/60 hover:border-white/40 hover:text-white"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <div className="divide-y divide-white/10 border-y border-white/10" data-testid="visa-list">
              {visible.map((v, i) => (
                <VisaRow key={v.id || `${v.country}-${v.category}`} v={v} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
